import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  CubeIcon, 
  TruckIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: adminAPI.getDashboardStats,
  });

  const { data: recentShipments, isLoading: shipmentsLoading } = useQuery({
    queryKey: ['recent-shipments'],
    queryFn: () => adminAPI.getShipments({ page: 1, limit: 5 }),
  });

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: adminAPI.deleteShipment,
    onSuccess: () => {
      toast.success('Shipment deleted successfully');
      queryClient.invalidateQueries(['recent-shipments']);
      queryClient.invalidateQueries(['dashboard-stats']);
    },
    onError: () => {
      toast.error('Failed to delete shipment');
    }
  });

  const handleDelete = (id, trackingNumber) => {
    if (window.confirm(`Delete shipment ${trackingNumber}? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const statCards = [
    {
      title: 'Total Shipments',
      value: stats?.data?.totalShipments || 0,
      icon: CubeIcon,
      color: 'bg-[#123B63]',
      change: '+12% from last month'
    },
    {
      title: 'In Transit',
      value: stats?.data?.inTransit || 0,
      icon: TruckIcon,
      color: 'bg-[#2563EB]',
      change: 'Active shipments'
    },
    {
      title: 'Delivered',
      value: stats?.data?.delivered || 0,
      icon: CheckCircleIcon,
      color: 'bg-[#16A34A]',
      change: 'This month'
    },
    {
      title: 'Exceptions',
      value: stats?.data?.exceptions || 0,
      icon: ExclamationTriangleIcon,
      color: 'bg-[#DC2626]',
      change: 'Requires attention'
    }
  ];

  const quickActions = [
    {
      title: 'Create New Shipment',
      description: 'Add a new package to the system',
      icon: PlusIcon,
      link: '/admin/shipments/create',
      color: 'bg-[#FF6B00] hover:bg-[#e65f00]'
    },
    {
      title: 'View All Shipments',
      description: 'Manage existing shipments',
      icon: EyeIcon,
      link: '/admin/shipments',
      color: 'bg-[#123B63] hover:bg-[#0E2D4F]'
    }
  ];

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0B1F3A]">Admin Dashboard</h1>
          <p className="text-[#64748B] mt-2">
            Manage shipments, track packages, and monitor system performance
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="card">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-[#64748B]">{stat.title}</p>
                  <p className="text-2xl font-bold text-[#0B1F3A]">{stat.value.toLocaleString()}</p>
                  <p className="text-xs text-[#64748B]">{stat.change}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="card">
              <h2 className="text-xl font-bold text-[#0B1F3A] mb-4">Quick Actions</h2>
              <div className="space-y-3">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.link}
                    className={`block p-4 rounded-lg text-white transition-colors duration-200 ${action.color}`}
                  >
                    <div className="flex items-center">
                      <action.icon className="h-6 w-6 mr-3" />
                      <div>
                        <h3 className="font-semibold">{action.title}</h3>
                        <p className="text-sm opacity-90">{action.description}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* System Status */}
            <div className="card mt-6">
              <h2 className="text-xl font-bold text-[#0B1F3A] mb-4">System Status</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#64748B]">API Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-[#16A34A]">
                    Online
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#64748B]">Database</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-[#16A34A]">
                    Connected
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#64748B]">Email Service</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-[#16A34A]">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#64748B]">Today's Shipments</span>
                  <span className="font-semibold text-[#0B1F3A]">
                    {stats?.data?.todayShipments || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Shipments */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0B1F3A]">Recent Shipments</h2>
                <Link
                  to="/admin/shipments"
                  className="text-[#FF6B00] hover:text-[#e65f00] text-sm font-medium"
                >
                  View all →
                </Link>
              </div>

              {shipmentsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B00]"></div>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tracking #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentShipments?.data?.shipments?.map((shipment) => (
                        <tr key={shipment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-[#0B1F3A]">
                              {shipment.tracking_number}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-[#0B1F3A]">{shipment.recipient_name}</div>
                            <div className="text-sm text-[#64748B]">
                              {shipment.recipient_city}, {shipment.recipient_country}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`status-badge status-${shipment.current_status}`}>
                              {shipment.current_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(shipment.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-3">
                              <Link
                                to={`/admin/shipments/${shipment.id}`}
                                className="text-[#FF6B00] hover:text-[#e65f00]"
                              >
                                View
                              </Link>
                              <button
                                onClick={() => handleDelete(shipment.id, shipment.tracking_number)}
                                disabled={deleteMutation.isPending}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                              >
                                <TrashIcon className="h-4 w-4 inline" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) || (
                        <tr>
                          <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                            No shipments found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
